// ApplicantsPoolScreen.js — All Applicants Pool (no job filter)
// Full-featured screen showing every applicant across all jobs
// Navigates to ApplicantDetailsScreen on tap

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View, Text, StyleSheet, SafeAreaView, FlatList, TouchableOpacity,
  TextInput, Image, Modal, ActivityIndicator, Alert, Dimensions,
  RefreshControl, Animated, StatusBar, Platform, ScrollView,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useFocusEffect } from '@react-navigation/native';
import {
  RecruitmentAPI, STAGES, STATUS_CFG, normaliseApplicant,
  avatarColor, initials, timeAgo, fmtDate, loadCompanyData,
} from './recruitmentShared';

const { width } = Dimensions.get('window');

const Avatar = ({ name, uri, size = 46 }) => {
  const [err, setErr] = React.useState(false);
  if (uri && !err) return (
    <View style={{ width: size, height: size, borderRadius: size / 2, overflow: 'hidden' }}>
      <Image source={{ uri }} style={{ width: size, height: size }} onError={() => setErr(true)} />
    </View>
  );
  return (
    <View style={{ width: size, height: size, borderRadius: size / 2, backgroundColor: avatarColor(name), alignItems: 'center', justifyContent: 'center' }}>
      <Text style={{ color: '#fff', fontSize: size * 0.32, fontWeight: '900' }}>{initials(name)}</Text>
    </View>
  );
};

export default function ApplicantsPoolScreen({ navigation, route }) {
  const [companyLogo, setCompanyLogo] = useState(route?.params?.companyLogo || null);
  const [companyName, setCompanyName] = useState(route?.params?.companyName || '');
  const [companyId, setCompanyId]     = useState(null);

  const [applicants, setApplicants]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [refreshing, setRefreshing]   = useState(false);
  const [search, setSearch]           = useState('');
  const [filterStage, setFilterStage] = useState('all');
  const [sortBy, setSortBy]           = useState('recent');
  const [selected, setSelected]       = useState(new Set());
  const [bulkMode, setBulkMode]       = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [saving, setSaving]           = useState(false);
  const [viewMode, setViewMode]       = useState('list'); // 'list' | 'compact'

  const fadeAnim = useRef(new Animated.Value(0)).current;

  useFocusEffect(useCallback(() => {
    loadCompanyData(setCompanyLogo, setCompanyName, setCompanyId);
  }, []));

  useEffect(() => { init(); }, []);

  const init = async () => {
    setLoading(true);
    // Never default to company 1 — silently leaks if storage key is missing
    const cid = parseInt(await AsyncStorage.getItem('company_id') || '0', 10) || null;
    if (!cid) {
      setLoading(false);
      Alert.alert('No company', 'Could not determine your company. Please sign in again.');
      return;
    }
    setCompanyId(cid);
    await fetchApplicants(cid);
    setLoading(false);
    Animated.timing(fadeAnim, { toValue: 1, duration: 400, useNativeDriver: true }).start();
  };

  const fetchApplicants = async (cid) => {
    try {
      const res = await RecruitmentAPI.getApplicants({ company_id: cid, limit: 500 });
      // New pipeline endpoint returns bare array; older shapes wrapped in data/rows/applications
      const raw = Array.isArray(res) ? res
                : Array.isArray(res?.data)         ? res.data
                : Array.isArray(res?.rows)         ? res.rows
                : Array.isArray(res?.applications) ? res.applications
                : [];
      setApplicants(raw.map(normaliseApplicant).filter(Boolean));
    } catch (e) {
      console.warn('Pool fetch error:', e?.message);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await fetchApplicants(companyId);
    setRefreshing(false);
  };

  // ── Computed ─────────────────────────────────────────────────────────────
  const sharedParams = { companyId, companyLogo, companyName };

  const filtered = (() => {
    let list = [...applicants];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(a =>
        String(a.name ?? '').toLowerCase().includes(q) ||
        String(a.email ?? '').toLowerCase().includes(q) ||
        a.jobTitle?.toLowerCase().includes(q) ||
        a.currentRole?.toLowerCase().includes(q) ||
        a.currentCompany?.toLowerCase().includes(q)
      );
    }
    if (filterStage !== 'all') {
      list = list.filter(a => (a.stage || '').toLowerCase() === filterStage);
    }
    switch (sortBy) {
      case 'recent':  list.sort((a, b) => new Date(b.appliedAt || 0) - new Date(a.appliedAt || 0)); break;
      case 'name':    list.sort((a, b) => a.name.localeCompare(b.name)); break;
      case 'score':   list.sort((a, b) => (b.aiScore || 0) - (a.aiScore || 0)); break;
      case 'rating':  list.sort((a, b) => (b.rating || 0) - (a.rating || 0)); break;
    }
    return list;
  })();

  const stageCount = (s) => applicants.filter(a => (a.stage || '').toLowerCase() === s).length;

  // ── Bulk actions ──────────────────────────────────────────────────────────
  const toggleSelect = (id) => {
    setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  };

  const handleBulkStage = async (stage) => {
    setSaving(true);
    try {
      await RecruitmentAPI.bulkUpdateStage([...selected], stage);
      setApplicants(prev => prev.map(a => selected.has(a.id) ? { ...a, stage } : a));
      setSelected(new Set());
      setBulkMode(false);
      setShowBulkModal(false);
      Alert.alert('Done', `${selected.size} applicants moved to ${stage}`);
    } catch (e) {
      Alert.alert('Error', e.message || 'Failed to update');
    } finally { setSaving(false); }
  };

  const handleBulkDelete = () => {
    Alert.alert('Delete', `Remove ${selected.size} applicants?`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: async () => {
        setSaving(true);
        try {
          await RecruitmentAPI.bulkDelete([...selected]);
          setApplicants(prev => prev.filter(a => !selected.has(a.id)));
          setSelected(new Set()); setBulkMode(false);
        } catch (e) { Alert.alert('Error', e.message); }
        finally { setSaving(false); }
      }},
    ]);
  };

  // ── Quick stage update ────────────────────────────────────────────────────
  const quickUpdateStage = async (applicant, stage) => {
    try {
      await RecruitmentAPI.updateStage(applicant.id, stage);
      setApplicants(prev => prev.map(a => a.id === applicant.id ? { ...a, stage } : a));
    } catch (_) {}
  };

  // ── Renders ───────────────────────────────────────────────────────────────
  const renderHeader = () => (
    <LinearGradient colors={['#0e4e96', '#1e7be2']} style={{ paddingBottom: 10 }}>
      <View style={S.headerRow}>
        <TouchableOpacity style={S.hBtn} onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={20} color="#fff" />
        </TouchableOpacity>
        <View style={{ flex: 1, marginLeft: 10 }}>
          <Text style={S.hTitle}>Applicant Pool</Text>
          <Text style={S.hSub}>{applicants.length} total · All jobs</Text>
        </View>
        {companyLogo
          ? <Image source={{ uri: companyLogo }} style={S.hLogo} />
          : <View style={S.hLogoFb}><Text style={{ color: '#fff', fontWeight: '800' }}>{(companyName || 'C')[0]}</Text></View>}
        <TouchableOpacity style={[S.hBtn, { marginLeft: 8 }]} onPress={() => setBulkMode(!bulkMode)}>
          <MaterialCommunityIcons name={bulkMode ? 'close' : 'checkbox-multiple-outline'} size={20} color="#fff" />
        </TouchableOpacity>
      </View>

      {/* Stage filter chips */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingHorizontal: 14, gap: 8 }}>
        {[{ key: 'all', label: `All (${applicants.length})`, color: '#fff' }, ...STAGES.map(s => ({ key: s.key, label: `${s.label} (${stageCount(s.key)})`, color: s.color }))].map(item => (
          <TouchableOpacity key={item.key}
            style={[S.stageChip, filterStage === item.key && S.stageChipActive]}
            onPress={() => setFilterStage(item.key)}>
            <Text style={[S.stageChipText, filterStage === item.key && { color: '#fff' }]}>{item.label}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </LinearGradient>
  );

  const renderToolbar = () => (
    <View style={S.toolbar}>
      <View style={S.searchWrap}>
        <Ionicons name="search" size={16} color="#9CA3AF" />
        <TextInput style={S.searchInput} value={search} onChangeText={setSearch}
          placeholder="Search by name, job, company..." placeholderTextColor="#9CA3AF" />
        {search.length > 0 && <TouchableOpacity onPress={() => setSearch('')}><Ionicons name="close-circle" size={16} color="#9CA3AF" /></TouchableOpacity>}
      </View>

      <View style={{ flexDirection: 'row', gap: 8, paddingHorizontal: 14, paddingVertical: 6 }}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 8 }}>
          {[
            { id: 'recent', label: 'Newest' },
            { id: 'name',   label: 'Name' },
            { id: 'score',  label: 'AI Score' },
            { id: 'rating', label: 'Rating' },
          ].map(s => (
            <TouchableOpacity key={s.id} style={[S.sortChip, sortBy === s.id && S.sortChipActive]}
              onPress={() => setSortBy(s.id)}>
              <Text style={[S.sortChipText, sortBy === s.id && { color: '#0e4e96', fontWeight: '700' }]}>{s.label}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
        <Text style={{ fontSize: 12, color: '#9CA3AF', alignSelf: 'center' }}>{filtered.length} results</Text>
      </View>
    </View>
  );

  const renderBulkBar = () => {
    if (!bulkMode || selected.size === 0) return null;
    return (
      <View style={S.bulkBar}>
        <Text style={{ color: '#fff', fontWeight: '700', fontSize: 13 }}>{selected.size} selected</Text>
        <View style={{ flex: 1 }} />
        <TouchableOpacity style={S.bulkBtn} onPress={() => setShowBulkModal(true)}>
          <MaterialCommunityIcons name="pipe" size={16} color="#fff" />
          <Text style={S.bulkBtnText}>Move</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[S.bulkBtn, { backgroundColor: '#EF4444' }]} onPress={handleBulkDelete}>
          <MaterialCommunityIcons name="delete" size={16} color="#fff" />
          <Text style={S.bulkBtnText}>Delete</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={() => { setSelected(new Set()); setBulkMode(false); }}>
          <Ionicons name="close" size={20} color="#fff" />
        </TouchableOpacity>
      </View>
    );
  };

  const renderCard = ({ item }) => {
    const cfg = STATUS_CFG[item.stage?.charAt(0).toUpperCase() + item.stage?.slice(1)] || STATUS_CFG.New;
    const isSelected = selected.has(item.id);
    return (
      <TouchableOpacity
        style={[S.card, isSelected && S.cardSelected, item.isFlagged && S.cardFlagged]}
        onPress={() => bulkMode ? toggleSelect(item.id) : navigation.navigate('ApplicantDetailsScreen', { ...sharedParams, applicant: item, applicantId: item.id })}
        onLongPress={() => { setBulkMode(true); toggleSelect(item.id); }}
        activeOpacity={0.85}
      >
        <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
          {bulkMode && (
            <TouchableOpacity onPress={() => toggleSelect(item.id)} style={{ marginRight: 10, marginTop: 2 }}>
              <MaterialCommunityIcons name={isSelected ? 'checkbox-marked-circle' : 'checkbox-blank-circle-outline'}
                size={22} color={isSelected ? '#0e4e96' : '#D1D5DB'} />
            </TouchableOpacity>
          )}
          <Avatar name={item.name} uri={item.avatar} size={46} />
          <View style={{ flex: 1, marginLeft: 12 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <Text style={S.cardName} numberOfLines={1}>{item.name}</Text>
              {item.isBookmarked && <MaterialCommunityIcons name="bookmark" size={14} color="#F59E0B" style={{ marginLeft: 4 }} />}
              {item.isFlagged && <MaterialCommunityIcons name="flag" size={14} color="#EF4444" style={{ marginLeft: 2 }} />}
            </View>
            <Text style={S.cardRole} numberOfLines={1}>{item.currentRole || item.jobTitle || '—'}</Text>
            <Text style={S.cardCompany} numberOfLines={1}>
              {[item.currentCompany, item.location].filter(Boolean).join(' · ') || '—'}
            </Text>
          </View>
          <View style={{ alignItems: 'flex-end', gap: 4 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: cfg.bg, borderRadius: 8, paddingHorizontal: 8, paddingVertical: 3 }}>
              <MaterialCommunityIcons name={cfg.icon} size={11} color={cfg.color} />
              <Text style={{ fontSize: 10, color: cfg.color, fontWeight: '700', marginLeft: 3 }}>
                {item.stage?.charAt(0).toUpperCase() + item.stage?.slice(1) || 'New'}
              </Text>
            </View>
            {item.aiScore != null && (
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <MaterialCommunityIcons name="robot-outline" size={11} color="#8B5CF6" />
                <Text style={{ fontSize: 11, color: '#8B5CF6', fontWeight: '700', marginLeft: 3 }}>{item.aiScore}%</Text>
              </View>
            )}
            {item.rating > 0 && (
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <Ionicons name="star" size={11} color="#F59E0B" />
                <Text style={{ fontSize: 11, color: '#F59E0B', fontWeight: '700', marginLeft: 2 }}>{item.rating}</Text>
              </View>
            )}
          </View>
        </View>

        {/* Tags */}
        {item.tags?.length > 0 && (
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 8 }}>
            {item.tags.map((tag, i) => (
              <View key={i} style={S.tag}>
                <Text style={S.tagText}>{tag}</Text>
              </View>
            ))}
          </ScrollView>
        )}

        {/* Footer */}
        <View style={S.cardFooter}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
            <Ionicons name="time-outline" size={12} color="#9CA3AF" />
            <Text style={S.footerMeta}>{item.appliedAt ? timeAgo(item.appliedAt) : '—'}</Text>
          </View>
          {item.jobTitle && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, flex: 1 }}>
              <MaterialCommunityIcons name="briefcase-outline" size={12} color="#9CA3AF" />
              <Text style={[S.footerMeta, { flex: 1 }]} numberOfLines={1}>{item.jobTitle}</Text>
            </View>
          )}

          {/* Quick stage move */}
          {!bulkMode && (
            <View style={{ flexDirection: 'row', gap: 6 }}>
              {item.stage !== 'shortlisted' && (
                <TouchableOpacity style={S.quickBtn} onPress={() => quickUpdateStage(item, 'shortlisted')}>
                  <MaterialCommunityIcons name="account-star" size={13} color="#F59E0B" />
                </TouchableOpacity>
              )}
              {item.stage !== 'rejected' && (
                <TouchableOpacity style={[S.quickBtn, { borderColor: '#FCA5A5' }]} onPress={() => quickUpdateStage(item, 'rejected')}>
                  <MaterialCommunityIcons name="close" size={13} color="#EF4444" />
                </TouchableOpacity>
              )}
              <TouchableOpacity style={[S.quickBtn, { borderColor: '#BFDBFE', paddingHorizontal: 8 }]}
                onPress={() => navigation.navigate('ApplicantDetailsScreen', { ...sharedParams, applicant: item, applicantId: item.id })}>
                <Text style={{ fontSize: 11, color: '#0e4e96', fontWeight: '700' }}>View</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      </TouchableOpacity>
    );
  };

  const renderBulkModal = () => (
    <Modal visible={showBulkModal} transparent animationType="slide" onRequestClose={() => setShowBulkModal(false)}>
      <View style={S.bsOverlay}>
        <View style={S.bsContent}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 16 }}>
            <Text style={S.bsTitle}>Move {selected.size} to Stage</Text>
            <TouchableOpacity onPress={() => setShowBulkModal(false)}><Ionicons name="close" size={24} color="#6B7280" /></TouchableOpacity>
          </View>
          {STAGES.map(s => (
            <TouchableOpacity key={s.key} style={S.stageOpt} onPress={() => handleBulkStage(s.key)} disabled={saving}>
              <View style={[S.stageOptIcon, { backgroundColor: s.color + '20' }]}>
                <MaterialCommunityIcons name={s.icon} size={20} color={s.color} />
              </View>
              <Text style={S.stageOptLabel}>{s.label}</Text>
              {saving && <ActivityIndicator size={14} color={s.color} />}
            </TouchableOpacity>
          ))}
        </View>
      </View>
    </Modal>
  );

  if (loading) return (
    <SafeAreaView style={{ flex: 1, backgroundColor: '#F3F4F6', justifyContent: 'center', alignItems: 'center' }}>
      <ActivityIndicator size={40} color="#0e4e96" />
      <Text style={{ marginTop: 14, color: '#9CA3AF', fontSize: 15 }}>Loading applicant pool...</Text>
    </SafeAreaView>
  );

  return (
    <SafeAreaView style={S.container}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
      {renderHeader()}
      {renderToolbar()}
      {renderBulkBar()}
      <Animated.View style={{ flex: 1, opacity: fadeAnim }}>
        <FlatList
          data={filtered}
          keyExtractor={i => i.id}
          renderItem={renderCard}
          contentContainerStyle={{ padding: 14, paddingBottom: 30 }}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#0e4e96']} />}
          ListEmptyComponent={
            <View style={{ alignItems: 'center', paddingVertical: 60 }}>
              <MaterialCommunityIcons name="account-search-outline" size={60} color="#D1D5DB" />
              <Text style={{ fontSize: 17, fontWeight: '700', color: '#374151', marginTop: 16 }}>No applicants found</Text>
              <Text style={{ fontSize: 14, color: '#9CA3AF', marginTop: 6 }}>
                <Text>{search ? 'Try a different search' : 'Applicants will appear here'}</Text>
              </Text>
            </View>
          }
        />
      </Animated.View>
      {renderBulkModal()}
    </SafeAreaView>
  );
}

const S = StyleSheet.create({
  container:    { flex: 1, backgroundColor: '#F3F4F6' },
  headerRow:    { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingTop: Platform.OS === 'android' ? 28 : 8, paddingBottom: 10 },
  hBtn:         { width: 36, height: 36, borderRadius: 18, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center' },
  hTitle:       { fontSize: 17, fontWeight: '900', color: '#fff' },
  hSub:         { fontSize: 11, color: 'rgba(255,255,255,0.75)', marginTop: 1 },
  hLogo:        { width: 32, height: 32, borderRadius: 16, borderWidth: 2, borderColor: 'rgba(255,255,255,0.5)' },
  hLogoFb:      { width: 32, height: 32, borderRadius: 16, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center' },
  stageChip:    { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, backgroundColor: 'rgba(255,255,255,0.2)', marginBottom: 4 },
  stageChipActive: { backgroundColor: 'rgba(255,255,255,0.9)' },
  stageChipText: { fontSize: 12, color: 'rgba(255,255,255,0.9)', fontWeight: '600' },

  toolbar:      { backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#E5E7EB' },
  searchWrap:   { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 9, borderBottomWidth: 1, borderBottomColor: '#F3F4F6', gap: 8 },
  searchInput:  { flex: 1, fontSize: 14, color: '#111827' },
  sortChip:     { paddingHorizontal: 12, paddingVertical: 5, borderRadius: 14, backgroundColor: '#F3F4F6', borderWidth: 1, borderColor: '#E5E7EB' },
  sortChipActive: { backgroundColor: '#EFF6FF', borderColor: '#BFDBFE' },
  sortChipText: { fontSize: 12, color: '#6B7280', fontWeight: '500' },

  bulkBar:      { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 14, paddingVertical: 10, backgroundColor: '#1E293B' },
  bulkBtn:      { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 12, paddingVertical: 7, borderRadius: 8, backgroundColor: '#374151' },
  bulkBtnText:  { color: '#fff', fontSize: 12, fontWeight: '700' },

  card:         { backgroundColor: '#fff', borderRadius: 14, padding: 14, marginBottom: 10, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 4, elevation: 2 },
  cardSelected: { borderWidth: 2, borderColor: '#3B82F6', backgroundColor: '#EFF6FF' },
  cardFlagged:  { borderLeftWidth: 3, borderLeftColor: '#EF4444' },
  cardName:     { fontSize: 15, fontWeight: '800', color: '#111827', flex: 1 },
  cardRole:     { fontSize: 12, color: '#6B7280', marginTop: 2 },
  cardCompany:  { fontSize: 11, color: '#9CA3AF', marginTop: 1 },
  cardFooter:   { flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 10, paddingTop: 10, borderTopWidth: 1, borderTopColor: '#F8FAFC' },
  footerMeta:   { fontSize: 11, color: '#9CA3AF' },
  tag:          { backgroundColor: '#F3E8FF', borderRadius: 8, paddingHorizontal: 8, paddingVertical: 3, marginRight: 6 },
  tagText:      { fontSize: 10, color: '#8B5CF6', fontWeight: '600' },
  quickBtn:     { width: 28, height: 28, borderRadius: 8, borderWidth: 1, borderColor: '#E5E7EB', alignItems: 'center', justifyContent: 'center' },

  bsOverlay:    { flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', justifyContent: 'flex-end' },
  bsContent:    { backgroundColor: '#fff', borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: 22, paddingBottom: Platform.OS === 'ios' ? 36 : 24 },
  bsTitle:      { fontSize: 17, fontWeight: '800', color: '#111827' },
  stageOpt:     { flexDirection: 'row', alignItems: 'center', paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#F8FAFC' },
  stageOptIcon: { width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center', marginRight: 14 },
  stageOptLabel:{ flex: 1, fontSize: 15, color: '#111827', fontWeight: '600' },
});
